class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.enforceDrawerVisibilityState(false);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Initialize shipping button functionality
    this.initializeShippingButton();
    
    // Initialize shipping drawer close button
    this.initializeShippingCloseButton();
    
    // Initialize overlay handlers
    this.initializeOverlayHandlers();
  }

  setHeaderCartIconAccessibility() {
    const cartDrawer = this;
    document.querySelectorAll('#cart-icon-bubble, [data-omx-wishlist-drawer-cart]').forEach((cartLink) => {
      if (!cartLink || cartLink.dataset.omxCartDrawerBound === '1') return;
      cartLink.dataset.omxCartDrawerBound = '1';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        cartDrawer.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          cartDrawer.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.enforceDrawerVisibilityState(true);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.enforceDrawerVisibilityState(false);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  enforceDrawerVisibilityState(isOpen) {
    // Defensive fallback: inline styles can override .drawer/.drawer.active class rules.
    this.style.visibility = isOpen ? 'visible' : '';
    this.style.pointerEvents = isOpen ? 'auto' : '';
    this.style.display = '';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Use a longer timeout to ensure DOM is fully updated
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      // Re-initialize overlay handlers after content update
      this.initializeShippingCloseButton();
      this.initializeOverlayHandlers();
    }, 100); // Increased timeout to ensure DOM is ready
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeShippingButton() {
    // Only add the event listener once by checking if it already exists
    if (!this._shippingButtonInitialized) {
      // Use event delegation on the cart drawer itself
      // This way the event listener persists even when content is updated
      this.addEventListener('click', (e) => {
        if (e.target && e.target.closest('#shipping-info-trigger')) {
          e.preventDefault();
          
          // Toggle shipping drawer
          const drawer = document.getElementById('shipping-drawer');
          if (drawer && drawer.classList.contains('active')) {
            this.closeShippingDrawer();
          } else {
            this.openShippingDrawer();
          }
        }
      });
      
      this._shippingButtonInitialized = true;
    }
  }

  openShippingDrawer() {
    const drawer = document.getElementById('shipping-drawer');
    
    if (!drawer) {
      console.error('Shipping drawer not found!');
      return;
    }
    
    // Only open if cart drawer is open
    if (!this.classList.contains('active')) {
      return;
    }
    
    drawer.classList.add('active');
    this.showOverlay();
  }

  closeShippingDrawer(immediate = false) {
    const drawer = document.getElementById('shipping-drawer');
    
    if (drawer) {
      if (immediate) {
        // Immediate close without animation
        drawer.style.transition = 'none';
        drawer.classList.remove('active');
        this.hideOverlay();
        // Restore transition after immediate close
        setTimeout(() => {
          drawer.style.transition = '';
        }, 10);
      } else {
        drawer.classList.remove('active');
        // Hide overlay after animation completes
        setTimeout(() => {
          this.hideOverlay();
        }, 300);
      }
    }
  }

  isMobile() {
    return window.innerWidth <= 749;
  }

  showOverlay() {
    const mobile = this.isMobile();
    
    if (mobile) {
      this.showMobileOverlay();
    } else {
      this.showCustomOverlay();
    }
  }

  hideOverlay() {
    if (this.isMobile()) {
      this.hideMobileOverlay();
    } else {
      this.hideCustomOverlay();
    }
  }

  showMobileOverlay() {
    const mobileOverlay = document.getElementById('cart-mobile-overlay');
    
    if (mobileOverlay) {
      mobileOverlay.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        background: rgba(0, 0, 0, 0.7) !important;
        z-index: 1000 !important;
        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: auto !important;
        display: block !important;
      `;
      
      mobileOverlay.classList.add('active');
    }
  }

  hideMobileOverlay() {
    const mobileOverlay = document.getElementById('cart-mobile-overlay');
    if (mobileOverlay) {
      mobileOverlay.style.opacity = '0';
      mobileOverlay.style.visibility = 'hidden';
      mobileOverlay.style.pointerEvents = 'none';
      
      mobileOverlay.classList.remove('active');
      
      setTimeout(() => {
        mobileOverlay.removeAttribute('style');
      }, 300);
      
    }
  }

  showCustomOverlay() {
    const overlay = document.getElementById('custom-overlay');
    const cartDrawerContainer = document.querySelector('#CartDrawer');
    
    if (overlay && cartDrawerContainer) {
      cartDrawerContainer.style.setProperty('background-color', 'transparent', 'important');
      overlay.classList.add('active');
    }
  }

  hideCustomOverlay() {
    const overlay = document.getElementById('custom-overlay');
    const cartDrawerContainer = document.querySelector('#CartDrawer');
    
    if (overlay) {
      overlay.classList.remove('active');
      overlay.style.width = '100vw';
    }
    
    if (cartDrawerContainer) {
      cartDrawerContainer.style.removeProperty('background-color');
    }
    
  }

  initializeShippingCloseButton() {
    const closeButton = document.getElementById('shipping-drawer-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeShippingDrawer();
      });
    }
  }

  initializeOverlayHandlers() {
    // Add click event to custom overlay for staged closing
    const customOverlay = document.getElementById('custom-overlay');
    if (customOverlay) {
      customOverlay.addEventListener('click', () => {
        this.closeBothDrawers();
      });
    }

    // Add click event to mobile overlay for closing shipping drawer
    const mobileOverlay = document.getElementById('cart-mobile-overlay');
    if (mobileOverlay) {
      mobileOverlay.addEventListener('click', () => {
        this.closeShippingDrawer();
      });
    }

    // Handle cart drawer overlay clicks with staged closing when shipping drawer is open
    const cartDrawerOverlay = document.getElementById('CartDrawer-Overlay');
    if (cartDrawerOverlay) {
      cartDrawerOverlay.addEventListener('click', () => {
        const shippingDrawer = document.getElementById('shipping-drawer');
        
        if (shippingDrawer && shippingDrawer.classList.contains('active')) {
          this.closeBothDrawers();
        } else {
          // Let cart drawer close naturally
        }
      });
    }

    // Additional safety: handle cart drawer close button clicks with staged approach
    const cartCloseButtons = this.querySelectorAll('.drawer__close');
    cartCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const shippingDrawer = document.getElementById('shipping-drawer');
        
        if (shippingDrawer && shippingDrawer.classList.contains('active')) {
          this.closeBothDrawers();
        } else {
          // Let cart drawer close naturally
        }
      });
    });
  }

  closeBothDrawers() {
    const shippingDrawer = document.getElementById('shipping-drawer');
    
    if (shippingDrawer && shippingDrawer.classList.contains('active')) {
      // Stage 1: Close shipping drawer first (slide back under cart)
      this.closeShippingDrawer();
      
      // Stage 2: Close cart drawer after shipping drawer finishes
      setTimeout(() => {
        if (this.classList.contains('active')) {
          this.close();
        }
        this.hideOverlay();
      }, 300); // Wait for shipping drawer animation to complete
    } else {
      // If shipping drawer not open, just close cart drawer normally
      if (this.classList.contains('active')) {
        this.close();
      }
      this.hideOverlay();
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.setHeaderCartIconAccessibility === 'function') {
    cartDrawer.setHeaderCartIconAccessibility();
  }
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
